import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiUsers, FiSearch } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import TrainingManager from '../../components/common/TrainingManager';
import { agentAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const LineTraining = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('clientId');
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    // If we have a clientId, we might want to fetch that specific agent's details to show their name
    // But TrainingManager handles the training list.
    // If no clientId, we need to fetch list of agents to allow selection.
    if (!clientId) {
      fetchClients();
    } else {
        // Fetch selected client details for display
        fetchClientDetails(clientId);
    }
  }, [clientId]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const { data } = await agentAPI.getAgents();
      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDetails = async (id) => {
      try {
          const { data } = await agentAPI.getAgent(id);
          setSelectedClient(data);
      } catch (error) {
          console.error("Error fetching client details", error);
      }
  }

  const handleClientSelect = (client) => {
    setSearchParams({ clientId: client.id });
    setSelectedClient(client);
  };

  const filteredClients = clients.filter(client => {
    const search = searchTerm.toLowerCase();
    const fullName = `${client.firstName} ${client.lastName}`.toLowerCase();
    return fullName.includes(search) || 
           (client.nickname || '').toLowerCase().includes(search) ||
           (client.agentCode || '').toLowerCase().includes(search);
  });

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-20 lg:pb-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">การฝึกอบรม</h1>
        <p className="text-gray-600">จัดการข้อมูลการฝึกอบรมของทีม</p>
      </div>

      {!clientId ? (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">กรุณาเลือกตัวแทนเพื่อจัดการการฝึกอบรม</h2>
          
          {/* Search */}
          <div className="relative mb-4">
             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiSearch} className="h-5 w-5 text-gray-400" />
             </div>
             <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="ค้นหาลูกทีม..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
             />
          </div>

          {loading ? (
             <LoadingSpinner />
          ) : (
             <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredClients.map(client => (
                   <button
                      key={client.id}
                      onClick={() => handleClientSelect(client)}
                      className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-indigo-50 hover:border-indigo-200 transition-colors flex items-center justify-between"
                   >
                      <div className="flex items-center">
                         <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                            <SafeIcon icon={FiUsers} className="w-5 h-5 text-indigo-600" />
                         </div>
                         <div>
                            <p className="font-medium text-gray-900">{client.firstName} {client.lastName}</p>
                            <p className="text-xs text-gray-500">{client.agentCode} | {client.nickname || '-'}</p>
                         </div>
                      </div>
                      <span className="text-xs text-indigo-600 font-medium">เลือก</span>
                   </button>
                ))}
                {filteredClients.length === 0 && (
                   <p className="text-center text-gray-500 py-4">ไม่พบข้อมูลตัวแทน</p>
                )}
             </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
           {/* Selected Client Header */}
           <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 flex justify-between items-center">
              <div className="flex items-center">
                 <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm mr-3">
                    <SafeIcon icon={FiUsers} className="w-5 h-5 text-indigo-600" />
                 </div>
                 <div>
                    <p className="text-sm text-indigo-800 font-medium">กำลังจัดการข้อมูลของ:</p>
                    <h3 className="text-lg font-bold text-indigo-900">
                        {selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : '...'}
                    </h3>
                 </div>
              </div>
              <button 
                onClick={() => {
                    setSearchParams({});
                    setSelectedClient(null);
                }}
                className="text-sm text-indigo-600 hover:text-indigo-800 underline"
              >
                เปลี่ยนคน
              </button>
           </div>

           <div className="bg-white rounded-lg shadow p-6">
              <TrainingManager 
                agentId={clientId} 
                isEditable={true} 
                showTitle={true}
              />
           </div>
        </div>
      )}
    </div>
  );
};

export default LineTraining;
